/**
 * Which save words to offer the person (docs/plans/os-voice-conversation.md
 * rule 12, C15; the 7 Oct fix list, item 4): the hint under Save, the
 * caption's "or say …" and the spoken nudge name the phrase in the language
 * and mix the person actually speaks to OS, not the app's setting alone.
 * Someone speaking English is told "save it", someone speaking Hinglish "save
 * karo", someone speaking Hindi in Devanagari "सेव करो".
 *
 * Read from the person's latest addressed lines (newest first); with none
 * yet, from the call's language setting. Pure, for the server and both
 * clients alike.
 */
import type { LiveRow } from './live-transcript';

/** The languages the save words are written in. */
export const SAVE_WORDS_LANGUAGES = ['en', 'hi', 'hi-Latn'] as const;
export type SaveWordsLanguage = (typeof SAVE_WORDS_LANGUAGES)[number];

/** How many of the person's latest lines decide. */
export const SAVE_WORDS_LINES = 3;

const DEVANAGARI = /[ऀ-ॿ]/;

/**
 * Words that mark Hindi written in Roman letters. Only words English never
 * uses as words of its own ("do", "me", "to", "hi", "sale" are left out), so
 * an English line is never mistaken for Hinglish.
 */
const HINGLISH = new Set([
  'haan',
  'han',
  'haa',
  'nahi',
  'nahin',
  'kya',
  'hai',
  'hain',
  'karo',
  'kar',
  'kardo',
  'karna',
  'mein',
  'mujhe',
  'ka',
  'ki',
  'ke',
  'ko',
  'aaj',
  'kal',
  'kitna',
  'kitne',
  'kitni',
  'bhai',
  'accha',
  'acha',
  'achha',
  'theek',
  'thik',
  'bolo',
  'dekho',
  'bana',
  'banao',
  'likh',
  'likho',
  'daal',
  'dalo',
  'wala',
  'wali',
  'yaar',
  'ji',
  'abhi',
  'aur',
  'bhi',
  'sab',
  'mera',
  'meri',
  'aap',
  'hum',
  'kuch',
  'kaise',
  'kaisa',
  'kab',
  'kaha',
  'kahan',
  'rupaye',
  'paisa',
  'paise',
  'baaki',
  'chalo',
  'ruko',
]);

/** The language of one line: Devanagari is Hindi, Roman with a Hindi word is Hinglish, other Roman is English; null with no letters. */
export const lineLanguageOf = (line: string): SaveWordsLanguage | null => {
  if (DEVANAGARI.test(line)) return 'hi';
  const words = line
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((word) => word !== '');
  if (words.length === 0) return null;
  return words.some((word) => HINGLISH.has(word)) ? 'hi-Latn' : 'en';
};

/** The save words' language for a call's setting: hi and hi-Latn as they are, any other English. */
export const saveWordsLanguageOfTag = (tag: string | null | undefined): SaveWordsLanguage =>
  tag === 'hi' || tag === 'hi-Latn' ? tag : 'en';

/**
 * One of the person's lines as the save words read it: its words, or its
 * words and the language they were spoken in. A line written in other
 * letters than it was said in (Devanagari in Roman on a Hinglish app), or one
 * stored translated between 7 and 12 Oct, carries `spoken`, and that decides,
 * not its letters.
 */
export type SpokenLine = string | { readonly text: string; readonly spoken?: SaveWordsLanguage };

const spokenOfLine = (line: SpokenLine): SaveWordsLanguage | null =>
  typeof line === 'string' ? lineLanguageOf(line) : (line.spoken ?? lineLanguageOf(line.text));

/**
 * The language to offer the save words in: the person's newest line that has
 * letters decides, then the one before, up to `SAVE_WORDS_LINES`; with none,
 * the call's setting. `lines` is oldest first, as a thread holds them.
 */
export const spokenLanguageOf = (lines: readonly SpokenLine[], fallback: string | null | undefined): SaveWordsLanguage => {
  for (const line of lines.slice(-SAVE_WORDS_LINES).reverse()) {
    const language = spokenOfLine(line);
    if (language !== null) return language;
  }
  return saveWordsLanguageOfTag(fallback);
};

/** Whether a value is one of the save words' languages. */
export const isSaveWordsLanguage = (value: unknown): value is SaveWordsLanguage => typeof value === 'string' && (SAVE_WORDS_LANGUAGES as readonly string[]).includes(value);

/**
 * A save-words key in a language: English is the bare key, every other sits
 * under it by tag (`agent.live.card.hint.hi-Latn`), as the spoken lines do.
 */
export const saveWordsKeyOf = <K extends string>(stem: K, language: SaveWordsLanguage): K | `${K}.hi` | `${K}.hi-Latn` =>
  language === 'en' ? stem : `${stem}.${language}`;

/**
 * The save words' language for a live transcript: the person's rows decide,
 * as `spokenLanguageOf` reads them, with the call's setting as the fallback.
 * Every client reads the transcript through this, so Build's hint and Save's
 * hint never disagree about the language.
 */
export const spokenLanguageOfTranscript = (rows: readonly LiveRow[], fallback: string | null | undefined): SaveWordsLanguage =>
  spokenLanguageOf(
    rows.flatMap((row) => (row.role === 'you' ? [{ text: row.text, ...(row.spoken === undefined ? {} : { spoken: row.spoken }) }] : [])),
    fallback,
  );
